import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Link, useLocation } from "react-router-dom";
import {
  HouseSimple, ChatsCircle, Users, MegaphoneSimple, CalendarBlank,
  Brain, Sparkle, ChartLineUp, Gear, SignOut, List, X, BookBookmark, Storefront,
  UserCircle, SlidersHorizontal, Receipt, WarningOctagon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLiveCounters } from "@/lib/useLiveCounters";
import { LogoMark } from "@/components/Logo";

const NAV = [
  { to: "/app/dashboard", key: "nav_dashboard", icon: HouseSimple },
  { to: "/app/inbox", key: "nav_inbox", icon: ChatsCircle },
  { to: "/app/human-inbox", key: "nav_human_inbox", icon: UserCircle },
  { to: "/app/crm", key: "nav_contacts", icon: Users },
  { to: "/app/campaigns", key: "nav_campaigns", icon: MegaphoneSimple },
  { to: "/app/orders", key: "nav_orders", icon: Storefront },
  { to: "/app/payments", key: "nav_payments", icon: Receipt },
  { to: "/app/complaints", key: "nav_complaints", icon: WarningOctagon },
  { to: "/app/calendar", key: "nav_calendar", icon: CalendarBlank },
  { to: "/app/agent", key: "nav_agent", icon: Brain },
  { to: "/app/knowledge", key: "nav_knowledge", icon: BookBookmark },
  { to: "/app/templates", key: "nav_templates", icon: Sparkle },
  { to: "/app/analytics", key: "nav_analytics", icon: ChartLineUp },
  { to: "/app/settings", key: "nav_settings", icon: Gear },
  { to: "/app/preferences", key: "nav_preferences", icon: SlidersHorizontal },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { ordersNew, apptsNew, humanNew, paymentsNew } = useLiveCounters(!!user);

  // Auto-close sidebar when route changes (mobile UX)
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  const seedDemo = async () => {
    try {
      const { data } = await api.post("/seed-demo");
      toast.success(`Demo cargada (${data.contacts_created} contactos)`);
      window.location.reload();
    } catch (e) {
      toast.error("Error al cargar demo");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#09090B] text-[#FAFAFA] relative">
      {/* Mobile hamburger toggle. z-20 so the inbox chat/insights panels (z-30) sit above it. */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        data-testid="mobile-menu-button"
        className="md:hidden fixed top-3 left-3 z-20 h-10 w-10 rounded-md bg-[#121214]/95 backdrop-blur border border-[#27272A] flex items-center justify-center text-white shadow-lg"
        aria-label="Abrir menu"
      >
        <List size={20} />
      </button>

      {/* Backdrop (mobile only when sidebar open) */}
      <div
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-backdrop"
        className={`md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Sidebar: fixed on mobile (off-canvas), static on desktop */}
      <aside
        data-testid="sidebar"
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-[#09090B] border-r border-[#27272A] flex flex-col
          transition-transform duration-300 ease-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="px-6 py-6 border-b border-[#27272A] flex items-center justify-between">
          <Link to="/app/dashboard" className="flex items-center gap-2" data-testid="sidebar-brand">
            <LogoMark size={32} />
            <div>
              <div className="font-display font-medium text-base tracking-tight">
                <span className="text-[#5E6AD2]">Wazco</span> <span className="text-[#F97316]">Express</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Sales · WhatsApp</div>
            </div>
          </Link>
          {/* Mobile close button */}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-button"
            className="md:hidden p-1.5 rounded hover:bg-[#18181B] text-[#A1A1AA]"
            aria-label="Cerrar menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = loc.pathname.startsWith(item.to);
            const label = t(item.key);
            let badge = 0;
            if (item.to === "/app/orders") badge = ordersNew;
            if (item.to === "/app/calendar") badge = apptsNew;
            if (item.to === "/app/human-inbox") badge = humanNew;
            if (item.to === "/app/payments") badge = paymentsNew;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.key}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-[#18181B] text-white"
                    : "text-[#A1A1AA] hover:bg-[#18181B] hover:text-white"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                <span>{label}</span>
                {badge > 0 && (
                  <span
                    data-testid={`nav-badge-${item.key}`}
                    className={`ml-auto text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded-full text-white min-w-[20px] text-center ${
                      item.to === "/app/human-inbox" ? "bg-[#F59E0B]" : item.to === "/app/payments" ? "bg-[#10B981]" : "bg-[#5E6AD2]"
                    }`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
                {active && badge === 0 && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#5E6AD2]" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 space-y-2 border-t border-[#27272A]">
          <Button
            variant="ghost"
            size="sm"
            onClick={seedDemo}
            data-testid="seed-demo-button"
            className="w-full justify-start text-xs text-[#A1A1AA] hover:text-white hover:bg-[#18181B]"
          >
            <Sparkle size={14} className="mr-2" />{t("seed_demo")}
          </Button>
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-[#121214]">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-xs font-medium">
              {user?.full_name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-name">{user?.full_name}</div>
              <div className="text-[10px] text-[#71717A] truncate">{user?.company_name}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="p-1.5 rounded hover:bg-[#27272A] text-[#A1A1AA] hover:text-white"
              title={t("logout")}
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
